import { URLSearchParams } from "url";
import { Params } from "react-router-dom";
import { moduleType } from "regen-api-ts-client";

import {
  DatasourceId,
  DATASOURCES,
  LayerId,
  MapModes,
} from "../components/InteractiveMap/types";
import { FARM_WIDE_FIELD } from "./constants";

export const buildModulePath = (
  farmId: string,
  annum: number,
  fieldId: string,
  moduleId?: moduleType,
) => {
  if (moduleId === moduleType.DASHBOARD) {
    return `/farm/${farmId}/${annum}`;
  }
  const basePath = `/farm/${farmId}/${moduleId}/${annum}`;
  return fieldId === FARM_WIDE_FIELD.id
    ? basePath
    : `${basePath}/field/${fieldId}`;
};

export const buildInternalModuleEditPath = (
  farmId: string,
  moduleId: moduleType,
  annum: number,
  fieldId: string,
) => {
  const basePath = `/admin/farm/${farmId}/recommendations/${moduleId}/${annum}`;
  return fieldId === FARM_WIDE_FIELD.id
    ? basePath
    : `${basePath}/field/${fieldId}`;
};

// Path to the interactive map
export const buildMapPath = (
  farmId: string,
  annum: number,
  fieldId?: string,
) => {
  const basePath = `/farm/${farmId}/map/${annum}`;
  return fieldId ? `${basePath}/field/${fieldId}` : basePath;
};

// Interactive map path with datasource and layer data
export const buildBaseMapPath = (
  farmId: string,
  annum: number,
  fieldId: string,
  datasourceId: DatasourceId,
  layerId?: LayerId | undefined,
  layerDate?: string | undefined,
) => {
  const basePath = buildMapPath(farmId, annum, fieldId);
  if (datasourceId === DATASOURCES.field_boundaries.id) {
    return basePath;
  }
  if (layerDate && layerId) {
    return `${basePath}/${datasourceId}/${layerId}/${layerDate}`;
  }
  return layerId
    ? `${basePath}/${datasourceId}/${layerId}`
    : `${basePath}/${datasourceId}`;
};

type CombinedParams = {
  params: Params;
  searchParams: URLSearchParams;
};

// Interactive map path with datasource and layer data that preserves
// existing query params
export const buildInteractiveMapPath = (
  previousParams: CombinedParams,
  farmId: string,
  annum: number,
  fieldId: string,
  datasourceId: DatasourceId,
  layerId?: LayerId | undefined,
  layerDate?: string | undefined,
) => {
  const { params, searchParams } = previousParams;
  const isCompareMode =
    searchParams.get("mode") && searchParams.get("mode") === MapModes.COMPARE;

  // In the compare mode but not the compare map, sometimes we need to change the
  // base path but capture existing data in the search params
  if (isCompareMode) {
    // Capture the current state of the param for the compare map if the two were matching
    // before the navigation.
    if (params.annum && annum !== +params.annum && !searchParams.get("annum")) {
      searchParams.set("annum", params.annum);
    }
    if (
      params.fieldId &&
      fieldId !== params.fieldId &&
      !searchParams.get("field")
    ) {
      searchParams.set("field", params.fieldId);
    }
    if (
      params.datasourceId &&
      datasourceId !== params.datasourceId &&
      !searchParams.get("datasource")
    ) {
      searchParams.set("datasource", params.datasourceId);
    }
    if (
      params.layerId &&
      layerId !== params.layerId &&
      !searchParams.get("layer")
    ) {
      searchParams.set("layer", params.layerId);
    }
    if (
      params.layerDate &&
      layerDate !== params.layerDate &&
      !searchParams.get("date")
    ) {
      searchParams.set("date", params.layerDate);
    }
  }
  const pathSearchParams = searchParams.size
    ? `?${searchParams.toString()}`
    : "";
  return (
    buildBaseMapPath(farmId, annum, fieldId, datasourceId, layerId, layerDate) +
    pathSearchParams
  );
};

export const buildCompareMapPath = (
  previousParams: CombinedParams,
  newSearchParams: { [key: string]: string },
) => {
  const { params, searchParams } = previousParams;
  // Set all new search params
  for (const key in newSearchParams) {
    searchParams.set(key, newSearchParams[key]);
  }
  // Check if any new params are equal to that value in the path and delete them if so
  if (params.fieldId === searchParams.get("field")) {
    searchParams.delete("field");
  }
  if (params.annum === searchParams.get("annum")) {
    searchParams.delete("annum");
  }
  if (params.datasourceId === searchParams.get("datasource")) {
    searchParams.delete("datasource");
  }
  if (
    params.layerId === searchParams.get("layer") ||
    searchParams.get("datasource") === DATASOURCES.field_boundaries.id // no associated layers
  ) {
    searchParams.delete("layer");
  }
  if (
    params.layerDate === searchParams.get("date") ||
    (searchParams.get("datasource") &&
      searchParams.get("datasource") !== DATASOURCES.satellite_imagery.id) // dates only for imagery
  ) {
    searchParams.delete("date");
  }
  return `${location.pathname}?${searchParams.toString()}`;
};

// Path to the "Field List" map
export const buildFieldMapPath = (farmId: string, annum: number) => {
  return `/farm/${farmId}/field-map/${annum}`;
};

export const buildInternalImageryPath = (
  farmId: string,
  annum: number,
  fieldId: string,
  layer?: string,
) =>
  `/admin/farm/${farmId}/imagery/${annum}/field/${fieldId}${
    layer ? `/${layer}` : ""
  }`;

export const buildInternalFieldPath = (
  farmId: string,
  annum: number,
  fieldId?: string,
) => {
  const basePath = `/admin/farm/${farmId}/fields/${annum}`;
  return fieldId ? `${basePath}/field/${fieldId}` : basePath;
};
